import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
  confirmPasswordReset,
  Auth,
  User,
  UserCredential,
} from 'firebase/auth';
import { getCurrentLocale, onLanguageChanged } from '../i18n';
import type { AppLocale } from '../i18n/locale';

// [DIAG] Log Firebase env vars on every module load (remove once auth refresh is verified)
console.log('[firebase.ts] VITE_FIREBASE_API_KEY:', import.meta.env.VITE_FIREBASE_API_KEY ? '(set)' : '(blank)');

// Every field is env-driven so a non-production environment (e.g. staging) can
// point at its own Firebase project and keep its user pool isolated. Defaults
// are the production project, so production behaviour is unchanged when the
// optional vars are absent.
export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'the-domino-game.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'the-domino-game',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'the-domino-game.firebasestorage.app',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
};

let app: FirebaseApp | null = null;
let auth: Auth | null = null;

/**
 * Firebase Auth email/SMS template language for an app locale. Firebase's
 * supported-language list guarantees plain 'es'; the regional 'es-419' code
 * is not relied on. `auth.languageCode` only selects the language of
 * Firebase-hosted communications (e.g. the password-reset email) — it never
 * affects sign-in, sign-up, tokens, or the current session.
 */
export function firebaseLanguageFor(locale: AppLocale): string {
  return locale === 'es-419' ? 'es' : 'en';
}

/**
 * Initialize Firebase app (call once at startup)
 */
export function initFirebase(): Auth {
  if (!app) {
    console.log('[firebase.ts] initFirebase() — initializing app');
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    // Language seam: seed from the active app locale and keep it in sync on
    // runtime switches. Registered once inside this singleton guard, so
    // re-imports and repeat initFirebase() calls never stack listeners.
    auth.languageCode = firebaseLanguageFor(getCurrentLocale());
    onLanguageChanged(locale => {
      if (auth) auth.languageCode = firebaseLanguageFor(locale);
    });
    console.log('[firebase.ts] initFirebase() — auth instance created:', !!auth);
  }
  return auth!;
}

/**
 * Get the Auth instance (must call initFirebase first)
 */
export function getAuthInstance(): Auth {
  if (!auth) {
    return initFirebase();
  }
  return auth;
}

/**
 * Sign in with email and password
 */
export async function signIn(email: string, password: string): Promise<UserCredential> {
  const authInstance = getAuthInstance();
  return signInWithEmailAndPassword(authInstance, email, password);
}

/**
 * Create a new user with email and password, then set their display name.
 */
export async function signUp(email: string, password: string, displayName: string): Promise<UserCredential> {
  const authInstance = getAuthInstance();
  const credential = await createUserWithEmailAndPassword(authInstance, email, password);
  await updateProfile(credential.user, { displayName });
  return credential;
}

/**
 * Create a Firebase account WITHOUT setting a display name — used by the
 * 2-step email-registration flow, where the account is created at Step 2
 * ("Sign Up") before the username (Step 2's own field) is known yet. Call
 * setDisplayName() once the username is chosen. Distinct from signUp()
 * above, which the old single-step wizard still uses unchanged.
 */
export async function createAccount(email: string, password: string): Promise<UserCredential> {
  const authInstance = getAuthInstance();
  return createUserWithEmailAndPassword(authInstance, email, password);
}

/**
 * Set the current Firebase user's display name — called once the backend
 * has confirmed the chosen username, so Firebase's own displayName stays in
 * sync with it (resolveDisplayName's fallback chain reads this).
 */
export async function setDisplayName(username: string): Promise<void> {
  const authInstance = getAuthInstance();
  const user = authInstance.currentUser;
  if (!user) return;
  await updateProfile(user, { displayName: username });
}

/**
 * Request a password-reset email for the given address. Firebase sends the
 * same generic response for both a real and a non-existent account (no
 * user-enumeration signal), so callers should treat this call as
 * "requested" rather than branching on success/failure — see
 * ForgotPasswordScreen.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  const authInstance = getAuthInstance();
  await sendPasswordResetEmail(authInstance, email);
}

/**
 * Validate a password-reset oobCode from the emailed link (mode=resetPassword).
 * Resolves with the account's email if the code is valid; throws
 * (auth/invalid-action-code, auth/expired-action-code, etc.) otherwise.
 */
export async function verifyPasswordReset(oobCode: string): Promise<string> {
  const authInstance = getAuthInstance();
  return verifyPasswordResetCode(authInstance, oobCode);
}

/**
 * Complete a password reset using the emailed link's oobCode.
 */
export async function completePasswordReset(oobCode: string, newPassword: string): Promise<void> {
  const authInstance = getAuthInstance();
  await confirmPasswordReset(authInstance, oobCode, newPassword);
}

/**
 * Sign in with Google OAuth popup
 */
export async function signInWithGoogle(): Promise<UserCredential> {
  const authInstance = getAuthInstance();
  const provider = new GoogleAuthProvider();
  return signInWithPopup(authInstance, provider);
}

export async function signInWithApple(): Promise<UserCredential> {
  const authInstance = getAuthInstance();
  const provider = new OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');
  return signInWithPopup(authInstance, provider);
}

export async function signInWithFacebook(): Promise<UserCredential> {
  const authInstance = getAuthInstance();
  const provider = new FacebookAuthProvider();
  provider.addScope('email');
  return signInWithPopup(authInstance, provider);
}

/**
 * Synchronous, read-only view of the currently signed-in Firebase uid (null
 * when logged out or Firebase never initialized). Used by identity-guarded
 * write paths to verify, in the same synchronous tick as the request, that
 * acquired credentials still belong to the account that started an operation.
 */
export function getCurrentAuthUid(): string | null {
  return auth?.currentUser?.uid ?? null;
}

/**
 * Get the current user's Firebase ID token (refreshes if needed).
 * Returns null if no user is signed in.
 *
 * In dev mode, returns the selected DEV_TOKEN.
 */
export async function getAuthToken(): Promise<string | null> {
  // Dev mode: use the runtime-selected dev token
  const { getDevToken } = await import('../config/apiConfig');
  const devToken = getDevToken();
  console.log('[firebase.ts] getAuthToken() — devToken present:', !!devToken, '| auth present:', !!auth);
  if (devToken) {
    return devToken;
  }

  // If Firebase was never initialized (no API key), bail out cleanly.
  if (!auth) return null;

  // Wait for Firebase to determine the initial auth state before reading
  // currentUser. On first load Firebase restores the session from IndexedDB
  // asynchronously; currentUser is null until that completes. authStateReady()
  // resolves once (immediately if already done, or waits for the first
  // onAuthStateChanged event), so subsequent calls are effectively free.
  await auth.authStateReady();

  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken(/* forceRefresh */ false);
}

/**
 * Listen for auth state changes
 */
export function onAuthStateChanged(callback: (user: User | null) => void): () => void {
  const authInstance = getAuthInstance();
  return firebaseOnAuthStateChanged(authInstance, callback);
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<void> {
  const authInstance = getAuthInstance();
  return firebaseSignOut(authInstance);
}

/**
 * Get the current user (or null)
 */
export function getCurrentUser(): User | null {
  const authInstance = getAuthInstance();
  return authInstance.currentUser;
}
